<template>
  <v-row no-gutters class="ma-0 pa-0" justify="center">
    <v-col cols="12" class="px-4">
      <v-row no-gutters class="ma-0 pa-0 mb-3 pt-2" align="center" justify="space-between">
        <v-col>
          <p class="subtitle-1 font-weight-bold primary--text ma-0">
            Histórico clínico
          </p>
          <p v-if="activeFilterDescription" class="caption grey--text text--darken-1 ma-0">
            {{ activeFilterDescription }}
          </p>
        </v-col>
        <v-col cols="auto">
          <v-menu
            v-model="filterMenu"
            :close-on-content-click="false"
            offset-y
            left
            min-width="260"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :rounded="!_xsOnly()"
                :icon="_xsOnly()"
                :outlined="!_xsOnly()"
                :text="_xsOnly()"
                color="primary"
                class="text-none clinical-timeline-filter-button"
                :aria-label="_xsOnly() ? 'Filtrar histórico' : null"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="19" :class="{ 'mr-2': !_xsOnly() }">{{ mdiFilterVariant }}</v-icon>
                <template v-if="!_xsOnly()">Filtrar</template>
                <span v-if="_xsOnly() && activeFilterDescription" class="clinical-filter-indicator"></span>
              </v-btn>
            </template>
            <v-card>
              <v-list dense class="py-2">
                <v-subheader class="text-uppercase caption font-weight-bold">
                  Mostrar no histórico
                </v-subheader>
                <v-list-item
                  v-for="item in filterOptions"
                  :key="item.value"
                  @click="selectFilter(item.value)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon v-if="filter === item.value">
                    <v-icon small color="primary">{{ mdiCheck }}</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-divider class="my-2"></v-divider>
                <v-list-item @click="$emit('toggleHideDrafts')">
                  <v-list-item-content>
                    <v-list-item-title>{{ _getLabel("HIDE_DRAFTS") }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      :input-value="hideDrafts"
                      color="primary"
                      dense
                      hide-details
                      class="ma-0"
                      @click.stop="$emit('toggleHideDrafts')"
                    ></v-switch>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </v-col>
      </v-row>

      <v-row v-if="showTruncatedWarning" no-gutters class="ma-0 pa-0 mb-2">
        <v-col cols="12">
          <p class="ma-0 caption grey--text text--darken-1">
            <v-icon x-small color="grey darken-1" class="mr-1">
              {{ mdiInformationOutline }}
            </v-icon>
            Parte do histórico está carregada. Carregue mais registros para consultar todas as datas.
          </p>
        </v-col>
      </v-row>

      <v-alert v-if="hasLoadErrors" dense text type="warning" class="mt-2">
        Não foi possível carregar parte do histórico. Os registros exibidos foram preservados.
        <v-btn small text color="primary" class="text-none clinical-timeline-action" @click="$emit('retry')">Tentar novamente</v-btn>
      </v-alert>

      <v-row v-if="loading" no-gutters class="ma-0 pa-0" justify="center">
        <p class="text-center subtitle-2 grey--text text--darken-1">
          {{ _getLabel("LOADING") }}
        </p>
      </v-row>

      <v-row
        v-else-if="!timelineItems.length"
        no-gutters
        class="ma-0 pa-0"
        justify="center"
      >
        <p class="text-center subtitle-2 grey--text text--darken-1">
          {{ emptyMessage }}
        </p>
      </v-row>

      <div v-else class="clinical-timeline-groups">
        <section
          v-for="group in groupedTimelineItems"
          :key="group.key"
          class="timeline-date-group"
        >
          <div class="timeline-day">
            <span>{{ group.label }}</span>
          </div>
          <v-row class="timeline-grid ma-0">
            <v-col
              v-for="item in group.items"
              :key="item._type + '-' + item.id"
              cols="12"
              md="6"
              class="timeline-entry py-1"
            >
              <encounter-card
                v-if="item._type === 'encounter'"
                :encounter="item"
                @open="$emit('openEncounter', $event)"
              />
              <vaccination-card
                v-else-if="item._type === 'vaccination'"
                :vaccination="item"
                @open="$emit('openVaccination', $event)"
                @finalize="$emit('finalizeVaccination', $event)"
                @remove="$emit('removeVaccination', $event)"
              />
              <lab-exam-card
                v-else-if="item._type === 'lab_exam'"
                :labExam="item"
                @open="$emit('openLabExam', $event)"
                @finalize="$emit('finalizeLabExam', $event)"
                @remove="$emit('removeLabExam', $event)"
                @attachFile="$emit('attachLabExamFile', $event)"
                @openFile="$emit('openLabExamFile', $event)"
              />
              <hospitalization-card
                v-else-if="item._type === 'hospitalization'"
                :hospitalization="item"
                @open="$emit('openHospitalization', $event)"
                @addEvolution="$emit('addEvolution', $event)"
                @discharge="$emit('dischargeHospitalization', $event)"
                @remove="$emit('removeHospitalization', $event)"
              />
              <prescription-card
                v-else-if="item._type === 'prescription'"
                :prescription="item"
                @open="$emit('openPrescription', $event)"
              />
            </v-col>
          </v-row>
        </section>
      </div>

      <v-row
        v-if="showLoadMoreEncounters"
        no-gutters
        class="ma-0 pa-0 mt-2"
        justify="center"
      >
        <v-btn
          small
          text
          color="primary"
          class="text-none text-decoration-underline clinical-timeline-action"
          :loading="loadingMore"
          @click="loadMoreEncounters"
        >
          Carregar mais histórico
        </v-btn>
      </v-row>

      <div class="vetalfa-padding-bottom-notch" style="height: 20px"></div>
    </v-col>
  </v-row>
</template>

<script>
import { mdiCheck, mdiFilterVariant, mdiInformationOutline } from "@mdi/js";
import EncounterCard from "@/components/EncounterCard.vue";
import VaccinationCard from "@/components/VaccinationCard.vue";
import LabExamCard from "@/components/LabExamCard.vue";
import HospitalizationCard from "@/components/HospitalizationCard.vue";
import PrescriptionCard from "@/components/PrescriptionCard.vue";

/**
 * Cada filtro olha a sua própria lista para saber se o backend truncou a
 * página; em "Todos" basta uma lista truncada para o aviso aparecer.
 */
const FILTER_TO_TRUNCATED_KEY = {
  encounters: "encounters",
  vaccinations: "vaccinations",
  lab_exams: "labExams",
  hospitalizations: "hospitalizations",
  prescriptions: "prescriptions",
};

export default {
  name: "PatientTimeline",
  components: {
    "encounter-card": EncounterCard,
    "vaccination-card": VaccinationCard,
    "lab-exam-card": LabExamCard,
    "hospitalization-card": HospitalizationCard,
    "prescription-card": PrescriptionCard,
  },
  data() {
    return {
      appending: false,
      filterMenu: false,
      mdiCheck,
      mdiFilterVariant,
      mdiInformationOutline,
    };
  },
  props: {
    hasLoadErrors: { type: Boolean, default: false },
    items: {
      type: Array,
      default: () => [],
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
      required: false,
    },
    filter: {
      type: String,
      default: "all",
      required: false,
    },
    hideDrafts: {
      type: Boolean,
      default: false,
      required: false,
    },
    truncated: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    hasMoreEncounters: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  computed: {
    filterOptions() {
      return [
        { value: "all", text: this._getLabel("ALL_EVENTS") },
        { value: "encounters", text: this._getLabel("ENCOUNTERS_FILTER") },
        { value: "vaccinations", text: this._getLabel("VACCINATIONS_FILTER") },
        { value: "lab_exams", text: this._getLabel("LAB_EXAMS_FILTER") },
        { value: "hospitalizations", text: this._getLabel("HOSPITALIZATIONS_FILTER") },
        { value: "prescriptions", text: this._getLabel("PRESCRIPTIONS_FILTER") },
      ];
    },
    activeFilterDescription() {
      const active = this.filterOptions.find((item) => item.value === this.filter);
      const parts = [];
      if (active && active.value !== "all") parts.push(active.text);
      if (this.hideDrafts) parts.push(this._getLabel("HIDE_DRAFTS"));
      return parts.join(" • ");
    },
    timelineItems() {
      return this.items || [];
    },
    groupedTimelineItems() {
      return this.timelineItems.reduce((groups, item) => {
        const key = this.itemDayKey(item);
        const current = groups[groups.length - 1];
        if (current && current.key === key) {
          current.items.push(item);
        } else {
          groups.push({ key, label: this.itemDayLabel(item), items: [item] });
        }
        return groups;
      }, []);
    },
    itemDayKey() {
      return (item) => {
        const date = item && (item.date || item.applicationDate || item.requestDate || item.admissionDate || item.lastUpdate || item.createdAt);
        if (!date) return 'undated';
        const parsed = new Date(date);
        return Number.isNaN(parsed.getTime()) ? String(date) : `${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`;
      };
    },
    showTruncatedWarning() {
      const truncated = this.truncated || {};
      const key = FILTER_TO_TRUNCATED_KEY[this.filter];
      if (key) return !!truncated[key];
      return Object.keys(truncated).some((item) => !!truncated[item]);
    },
    showLoadMoreEncounters() {
      if (this.loading) return false;
      return !!this.hasMoreEncounters;
    },
    emptyMessage() {
      if (this.hasLoadErrors) return 'O histórico ainda não foi carregado.';
      if (this.filter === "vaccinations")
        return this._getLabel("NO_VACCINATION_ADDED");
      if (this.filter === "encounters")
        return this._getLabel("NO_ENCOUNTER_ADDED");
      if (this.filter === "lab_exams")
        return this._getLabel("NO_LAB_EXAM_ADDED");
      if (this.filter === "hospitalizations")
        return this._getLabel("NO_HOSPITALIZATION_ADDED");
      if (this.filter === "prescriptions")
        return this._getLabel("NO_PRESCRIPTION_ADDED_FOR_THIS_PATIENT");
      return this._getLabel("NO_TIMELINE_ITEMS");
    },
  },
  watch: {
    items() {
      // O carregamento incremental preserva a lista cronológica já exibida.
      this.appending = false;
    },
  },
  methods: {
    itemDayLabel(item) {
      const date = item && (item.date || item.applicationDate || item.requestDate || item.admissionDate || item.lastUpdate || item.createdAt);
      return date ? this._getDateFromTimestamp(date) : this._getLabel('DATE_NOT_INFORMED');
    },
    selectFilter(value) {
      this.$emit("filterChange", value);
      this.filterMenu = false;
    },
    loadMoreEncounters() {
      this.appending = true;
      this.$emit("loadMoreEncounters");
    },
  },
};
</script>
<style scoped>
.timeline-date-group {
  position: relative;
}
.timeline-day {
  align-items: center;
  color: #193153;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
  margin: 14px 0 4px;
  text-transform: uppercase;
}
.timeline-day::before {
  background: #193153;
  border: 3px solid #ffffff;
  border-radius: 50%;
  content: "";
  height: 14px;
  width: 14px;
  z-index: 1;
}
.timeline-day span {
  background: #ffffff;
  padding: 3px 4px;
}
.timeline-entry {
  display: flex;
}
.timeline-entry ::v-deep .timeline-card {
  height: 100%;
  width: 100%;
}
.clinical-timeline-filter-button,
.clinical-timeline-action {
  min-height: 44px;
}
.clinical-timeline-filter-button {
  min-width: 44px !important;
}
.clinical-filter-indicator {
  background: #00728a;
  border: 2px solid #ffffff;
  border-radius: 50%;
  height: 9px;
  position: absolute;
  right: 5px;
  top: 5px;
  width: 9px;
}
.timeline-entry ::v-deep .v-btn {
  min-height: 44px;
  min-width: 44px;
}
.timeline-entry ::v-deep .v-btn__content {
  font-size: 12px;
  letter-spacing: 0.2px;
}
@media (min-width: 960px) {
  .clinical-timeline-groups {
    border-top: 1px solid #eef1f5;
    padding-top: 2px;
  }
  .timeline-grid {
    margin-left: -8px !important;
    margin-right: -8px !important;
  }
  .timeline-entry {
    padding: 8px !important;
  }
}
@media (max-width: 959px) {
  .timeline-day {
    margin-left: 0;
  }
  .timeline-day::before {
    border-color: #f5f6fa;
  }
  .timeline-day span {
    background: #f5f6fa;
    border-radius: 999px;
    padding: 5px 10px;
  }
  .timeline-entry {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
</style>
